// HomeHeroComponent.jsx
// Home page hero and sections. Text content is loaded from the backend API (/home),
// while images remain served from the frontend public assets.

import React, { useEffect, useState, useMemo } from 'react';
import './Home.css';
import { useCatalogSettings, useTheme } from '../../App';
import { NavLink } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { getCategories, getFooterSettings } from '../../services/settingsCache';
import { motion } from 'framer-motion';

const ALLOW_FALLBACK_CONTENT = process.env.NODE_ENV !== 'production';

const DEFAULT_HOME_CONTENT = {
  hero: {
    badge: 'Trusted Since 1998',
    titleLines: ['Premium Construction', 'Materials for Your Dream', 'Projects'],
    subtitle:
      'Your one-stop destination for high-quality plywood, laminates, sanitary ware, and all decorative essentials. Quality materials, expert guidance, timely delivery.',
    imageUrl: '',
    primaryButton: {
      label: 'Explore Products',
      to: '/products',
    },
    secondaryButton: {
      label: 'Our Services',
      to: '/services#services-banner',
    },
  },
  features: [
    {
      key: 'quality',
      title: 'Quality Assured',
      text: 'ISO certified products with guaranteed quality standards',
    },
    {
      key: 'delivery',
      title: 'Fast Delivery',
      text: 'Quick delivery across all major cities and regions',
    },
    {
      key: 'support',
      title: 'Expert Support',
      text: 'Professional guidance from our experienced team',
    },
    {
      key: 'range',
      title: 'Wide Range',
      text: 'Comprehensive selection of construction materials',
    },
  ],
  categories: [],
  categorySection: {
    title: 'Explore Our Categories',
    subtitle: 'Discover our comprehensive range of premium construction materials',
  },
  featuredSection: {
    title: 'Featured Products',
    subtitle: 'Handpicked bestsellers from our collection',
    viewAllLabel: 'View All →',
  },
  featuredProducts: [
    {
      id: 1,
      name: 'Premium BWP Plywood 18mm',
      price: 'Rs. 332.49',
      currency: '',
      rating: '4.8',
      imageKey: 'pp1',
      imageUrl: `${process.env.PUBLIC_URL}/assets/images/pp1.png`,
    },
    {
      id: 2,
      name: 'Designer Laminate Collection',
      price: 'Rs. 312.49',
      currency: '',
      rating: '4.9',
      imageKey: 'pp2',
      imageUrl: `${process.env.PUBLIC_URL}/assets/images/pp2.png`,
    },
    {
      id: 3,
      name: 'Premium Sanitary Suite',
      price: 'Rs. 352.49',
      currency: '',
      rating: '4.7',
      imageKey: 'pp3',
      imageUrl: `${process.env.PUBLIC_URL}/assets/images/pp3.png`,
    },
  ],
  statsSection: {
    ariaLabel: 'Site statistics',
  },
  stats: [
    { value: '5000+', label: 'Happy Customers' },
    { value: '10000+', label: 'Products Delivered' },
    { value: '25+', label: 'Years Experience' },
    { value: '50+', label: 'Expert Team' },
  ],
  whyChoose: {
    title: 'Why Choose Tiwari Syndicate?',
    subtitle:
      "With over 25 years of excellence in the construction materials industry, we've built our reputation on quality, reliability, and customer satisfaction.",
    bulletPoints: [
      'ISO Certified Quality Standards',
      'Pan-India Delivery Network',
      'Expert Technical Support',
      'Competitive Pricing',
      'Genuine Products Guaranteed',
      'After-Sales Service',
    ],
    images: [{ imageUrl: '' }, { imageUrl: '' }, { imageUrl: '' }, { imageUrl: '' }],
  },
  ctaBanner: {
    title: 'Need Expert Advice?',
    subtitle:
      'Our team of construction material experts is here to help you choose the right products for your project. Get in touch today!',
    phone: {
      label: 'Call Us: 0621-406-9248',
      href: 'tel:+916214069248',
    },
    email: {
      label: 'Email enquiry',
      href: 'mailto:enquiry@tiwarisyndicate.com',
    },
  },
};

export default function HomeHeroComponent() {
  const { isNightMode } = useTheme();
  const { showProductRate } = useCatalogSettings();
  const [homeData, setHomeData] = useState(null);
  const [footerSettings, setFooterSettings] = useState(null);
  const [categoriesData, setCategoriesData] = useState([]);
  const [featuredProductsData, setFeaturedProductsData] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getFooterSettings().then(data => {
      if (!cancelled && data) setFooterSettings(data);
    }).catch(() => null);

    const fetchHomeContent = async () => {
      try {
        // Try to fetch from new content management API first
        const data = await apiClient.get('/content/home');
        if (!cancelled) setHomeData(data);
      } catch (err) {
        console.error('Home content load error from new API, trying fallback:', err);
        try {
          // Fallback to old API if new API fails
          const fallbackData = await apiClient.get('/home');
          if (!cancelled) setHomeData(fallbackData);
        } catch (fallbackErr) {
          console.error('Home content load error from fallback API:', fallbackErr);
          if (!cancelled) setHomeData(ALLOW_FALLBACK_CONTENT ? DEFAULT_HOME_CONTENT : null);
        }
      }
    };

    fetchHomeContent();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      try {
        const data = await getCategories();
        if (!cancelled) {
          setCategoriesData(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load categories for home page', err);
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFeaturedProducts = async () => {
      try {
        const data = await apiClient.get('/products?featured=true&limit=3&format=paginated&sort=latest');
        if (!cancelled) {
          setFeaturedProductsData(Array.isArray(data?.items) ? data.items : []);
        }
      } catch (err) {
        console.error('Failed to load featured products for home page', err);
      }
    };

    loadFeaturedProducts();

    return () => {
      cancelled = true;
    };
  }, []);


  const data = useMemo(() => {
    const rawData = homeData || (ALLOW_FALLBACK_CONTENT ? DEFAULT_HOME_CONTENT : null);
    if (!rawData) return null;
    
    // Adapter function to handle both old and new API structures
    const adaptHomeData = (rawData) => {
      // Check if it's the new API structure (has heroBadge, heroTitle, etc.)
      if (rawData.heroBadge || rawData.heroTitle) {
        return {
          hero: {
            badge: rawData.heroBadge || 'Trusted Since 1998',
            titleLines: (() => {
              const lines = rawData.heroTitle ? rawData.heroTitle.split('\n').filter(line => line.trim()) : ['Premium Construction', 'Materials for Your Dream', 'Projects'];
              return lines;
            })(),
            subtitle: rawData.heroSubtitle || 'Your one-stop destination for high-quality plywood, laminates, sanitary ware, and all construction essentials. Quality materials, expert guidance, timely delivery.',
            imageUrl: rawData.heroImageUrl || '',
            primaryButton: {
              label: rawData.heroPrimaryButton || 'Explore Products',
              to: '/products',
            },
            secondaryButton: {
              label: rawData.heroSecondaryButton || 'Our Services',
              to: '/services#services-banner',
            },
          },
          features: rawData.features || [
            { key: 'quality', title: 'Quality Assured', text: 'ISO certified products with guaranteed quality standards' },
            { key: 'delivery', title: 'Fast Delivery', text: 'Quick delivery across all major cities and regions' },
            { key: 'support', title: 'Expert Support', text: 'Professional guidance from our experienced team' },
            { key: 'range', title: 'Wide Range', text: 'Comprehensive selection of construction materials' }
          ],
          categories: rawData.categories || [],
          categorySection: {
            title: rawData.categoriesTitle || 'Explore Our Categories',
            subtitle: rawData.categoriesSubtitle || 'Discover our comprehensive range of premium construction materials',
          },
          featuredSection: {
            title: rawData.featuredTitle || 'Featured Products',
            subtitle: rawData.featuredSubtitle || 'Handpicked bestsellers from our collection',
            viewAllLabel: rawData.featuredViewAll || 'View All',
          },
          featuredProducts: (rawData.featuredProducts && rawData.featuredProducts.length > 0) ? rawData.featuredProducts : DEFAULT_HOME_CONTENT.featuredProducts,
          statsSection: rawData.statsSection || { ariaLabel: 'Site statistics' },
          stats: [
            { value: rawData.statsCustomers || '5000+', label: 'Happy Customers' },
            { value: rawData.statsProducts || '10000+', label: 'Products Delivered' },
            { value: rawData.statsExperience || '25+', label: 'Years Experience' },
            { value: rawData.statsProjects || '500+', label: 'Projects Completed' }
          ],
          whyChoose: rawData.whyChoose || DEFAULT_HOME_CONTENT.whyChoose,
          ctaBanner: rawData.ctaBanner || DEFAULT_HOME_CONTENT.ctaBanner,
          testimonials: rawData.testimonials || DEFAULT_HOME_CONTENT.testimonials,
          contactSection: rawData.contactSection || DEFAULT_HOME_CONTENT.contactSection,
        };
      } else {
        // Old API structure - use as-is
        
        return rawData;
      }
    };

    return adaptHomeData(rawData);
  }, [homeData]);

  if (!data) {
    return (
      <main
        style={{
          background: isNightMode ? '#020617' : '#ffffff',
          color: isNightMode ? '#f1f5f9' : '#111827',
          minHeight: '60vh',
          padding: 24,
          fontFamily: 'Arial, sans-serif',
        }}
      >
        Loading...
      </main>
    );
  }

  const {
    hero = {},
    features = [],
    categories = [],
    categorySection = {},
    featuredSection = {},
    featuredProducts = [],
    statsSection = {},
    stats = [],
    whyChoose = {},
    ctaBanner = {},
  } = data || {};

  const allCategories = categoriesData.length ? categoriesData : categories;
  const mainCategories = allCategories.filter(cat => !cat.parentCategory);
  const effectiveCategories = mainCategories.map((item, index) => {
    const defaultImages = [
      `${process.env.PUBLIC_URL}/assets/images/p1.png`,
      `${process.env.PUBLIC_URL}/assets/images/pp2.png`,
      `${process.env.PUBLIC_URL}/assets/images/pp3.png`,
      `${process.env.PUBLIC_URL}/assets/images/w1.png`,
      `${process.env.PUBLIC_URL}/assets/images/w2.png`,
      `${process.env.PUBLIC_URL}/assets/images/w3.png`,
    ];
    return {
      slug: item.slug || '',
      title: item.name || item.title || '',
      imageUrl: item.imageUrl || defaultImages[index % defaultImages.length],
    };
  });

  const heroBgImage = hero?.imageUrl || `${process.env.PUBLIC_URL}/assets/images/Container.png`;
  const effectiveFeaturedProducts = featuredProductsData.length
    ? featuredProductsData.map((item, index) => ({
        id: item._id,
        name: item.name,
        price: typeof item.price === 'number' ? `Rs. ${item.price.toFixed(2)}` : '',
        currency: '',
        rating: '4.8',
        imageUrl: item.imageUrl || `${process.env.PUBLIC_URL}/assets/images/pp1.png`,
      }))
    : featuredProducts;

  return (
    <section
      className="w-full overflow-hidden home-hero"
      style={{
        background: '#ffffff',
        border: isNightMode ? '1px solid #1f2937' : '1px solid #e5e7eb',
        minHeight: '100vh',
      }}
    >
      <div className="home-hero__media" style={{ position: 'relative', backgroundColor: '#020617' }}>
        <img
          src={heroBgImage}
          alt=""
          className="home-hero__image"
        />
        {/* Dark overlay for text readability */}
        <div
          className="home-hero__overlay"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.55) 55%, rgba(15,23,42,0.25) 100%)',
            zIndex: 1,
          }}
        />
        {/* Text content */}
        <motion.div
          className="home-hero__content"
          style={{
            position: 'absolute',
            zIndex: 5,
            top: 0,
            left: 0,
            right: 0,
            paddingLeft: '40px',
            paddingRight: '40px',
          }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.span
              style={{
                display: 'inline-block',
                padding: '6px 12px',
                borderRadius: '9999px',
                background: '#A7E08D',
                border: '0',
                color: '#000',
                fontFamily: 'Arial, sans-serif',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '16px',
                textTransform: 'none',
                letterSpacing: '0',
                marginBottom: '14px',
                textAlign: 'left',
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {hero?.badge || 'Trusted Since 1998'}
            </motion.span>
            <motion.h1
              className="hero__title"
              style={{ margin: '8px 0 16px', textTransform: 'none', textShadow: '0 2px 6px rgba(0,0,0,0.35)', textAlign: 'left' }}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {(hero?.titleLines || ['Premium Construction', 'Materials for Your Dream', 'Projects']).map((line, idx) => (
                <span key={idx} style={{ display: 'block' }}>
                  {line}
                </span>
              ))}
            </motion.h1>
            <motion.p
              className="hero__subtitle"
              style={{ margin: '0 0 0', textShadow: '0 1px 4px rgba(0,0,0,0.3)', textAlign: 'left' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 0.9, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {hero?.subtitle || 'Your one-stop destination for high-quality plywood, laminates, sanitary ware, and all construction essentials. Quality materials, expert guidance, timely delivery.'}
            </motion.p>
            <motion.div 
              className="home-hero__actions" 
              style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap', textAlign: 'left' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <NavLink
                to={hero?.primaryButton?.to || '/products'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                  padding: '0 16px',
                  borderRadius: '10px',
                  background: '#22c55e',
                  color: '#052e16',
                  fontWeight: 600,
                  textDecoration: 'none',
                  border: '1px solid #22c55e',
                  textTransform: 'none'
                }}
              >
                {hero?.primaryButton?.label || 'Explore Products'}
              </NavLink>
              <NavLink
                to={hero?.secondaryButton?.to || '/services'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                  padding: '0 16px',
                  borderRadius: '10px',
                  background: '#ffffff',
                  color: '#111827',
                  fontWeight: 600,
                  textDecoration: 'none',
                  border: '1px solid #e5e7eb',
                  textTransform: 'none'
                }}
              >
                {hero?.secondaryButton?.label || 'Our Services'}
              </NavLink>
            </motion.div>
          </div>
        </motion.div>
        {/* Curved white wave at bottom */}
        <div className="home-hero__wave" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 1 }}>
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ width: '100%', height: '120px', display: 'block' }}>
            <path d="M0,40 C360,140 1080,-20 1440,40 L1440,120 L0,120 Z" fill="#ffffff" />
          </svg>
        </div>
        
      </div>
      {/* Features section */}
      <motion.div 
        className="features"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="features__grid">
          {(features || []).map((item, index) => (
            <motion.div 
              key={item.key} 
              className="card" 
              style={{ textAlign: 'center' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  margin: '0 auto 12px',
                  borderRadius: '9999px',
                  background: 'rgba(34,197,94,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Icon kept purely frontend for now */}
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <div className="card__title">{item.title}</div>
              <div className="card__text">{item.text}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>
      {/* Categories section */}
      <motion.section 
        className="categories" 
        aria-labelledby="categories-title"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="categories__inner">
          <motion.h2 
            id="categories-title" 
            className="categories__title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {categorySection?.title || 'Explore Our Categories'}
          </motion.h2>
          <motion.p 
            className="categories__subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {categorySection?.subtitle || 'Discover our comprehensive range of premium construction materials'}
          </motion.p>
          <div className="categories__grid">
            {(effectiveCategories || []).map((cat, index) => (
              <motion.div
                key={cat.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <NavLink
                  className="category-card"
                  to={`/products/${cat.slug || 'default'}`}
                  style={{
                    backgroundImage: `url(${cat.imageUrl})`,
                  }}
                >
                  {cat.badge && (
                    <span className="category-card__badge">{cat.badge}</span>
                  )}
                  <div className="category-card__overlay"></div>
                  <div className="category-card__title">{cat.title}</div>
                </NavLink>
              </motion.div>
            ))}
            {effectiveCategories.length === 0 && (
              <div style={{ color: '#6b7280', fontSize: 14 }}>Loading categories...</div>
            )}
          </div>
        </div>
      </motion.section>
      {/* Featured Products */}
      <motion.section 
        className="featured" 
        aria-labelledby="featured-title"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="featured__inner">
          <motion.div 
            className="featured__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h2 id="featured-title" className="featured__title">
                {featuredSection?.title || 'Featured Products'}
              </h2>
              <p className="featured__subtitle">
                {featuredSection?.subtitle || 'Handpicked bestsellers from our collection'}
              </p>
            </div>
            <NavLink
              to="/products"
              className="featured__view"
              style={{
                display: 'inline-flex',
                padding: '6.003px 13.167px 8px 13.167px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                alignSelf: 'flex-end',
                borderRadius: '8px',
                border: '1.18px solid rgba(0, 0, 0, 0.10)',
                background: '#FFF',
                color: '#0A0A0A',
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                whiteSpace: 'nowrap',
                width: 'fit-content',
                minWidth: 'max-content',
                flexShrink: 0,
                textDecoration: 'none',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              <span style={{ whiteSpace: 'nowrap', display: 'inline-block', lineHeight: '20px' }}>
                {featuredSection?.viewAllLabel ? featuredSection.viewAllLabel.replace('→', '').trim() : 'View All'}
              </span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none"
                style={{ width: '15.989px', height: '15.989px', flexShrink: 0, display: 'block' }}
              >
                <path d="M3.33105 7.99438H12.6578" stroke="#0A0A0A" strokeWidth="1.33239" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.99438 3.33105L12.6577 7.99441L7.99438 12.6578" stroke="#0A0A0A" strokeWidth="1.33239" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </NavLink>
          </motion.div>
          <div className="featured__grid">
            {(effectiveFeaturedProducts || []).map((p, index) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <NavLink
                  to={`/product/${p.id || 'default'}`}
                  className="product-card"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div
                    className="product-card__image"
                    style={{
                      backgroundImage: index === 0 
                        ? `url(${process.env.PUBLIC_URL}/assets/images/p1.png)`
                        : index === 1
                        ? `url(${process.env.PUBLIC_URL}/assets/images/pp2.png)`
                        : index === 2
                        ? `url(${process.env.PUBLIC_URL}/assets/images/pp3.png)`
                        : p.imageUrl
                        ? `url(${p.imageUrl})`
                        : `url(${process.env.PUBLIC_URL}/assets/images/${p.imageKey}.png)`,
                    }}
                  />
                  <div className="product-card__body">
                    <div className="product-card__rating">
                      <span className="stars" aria-hidden="true">
                        ★★★★★
                      </span>
                      <span className="rating-value">{p.rating}</span>
                    </div>
                    <h3 className="product-card__name">{p.name}</h3>
                    <div className="product-card__price" style={{ minHeight: 24 }}>
                      {showProductRate ? (
                        <>
                          <span className="amount">{p.price}</span>
                          <span className="currency">{p.currency}</span>
                        </>
                      ) : (
                        <span className="amount">Contact for Price</span>
                      )}
                    </div>
                  </div>
                </NavLink>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
      <motion.section 
        className="stats" 
        aria-label={statsSection?.ariaLabel || 'Site statistics'}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="stats__inner">
          <div className="stats__grid">
            {(stats || []).map((item, idx) => (
              <motion.div
                key={idx}
                className="stat"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="stat__value">{item.value}</div>
                <div className="stat__label">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
      {/* Why Choose section */}
      <motion.section 
        className="why" 
        aria-labelledby="why-title"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="why__inner">
          <motion.div 
            className="why__content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 id="why-title" className="why__title">{whyChoose?.title || 'Why Choose Tiwari Syndicate?'}</h2>
            <p className="why__subtitle">{whyChoose?.subtitle || "With over 25 years of excellence in the construction materials industry, we've built our reputation on quality, reliability, and customer satisfaction."}</p>
            <ul className="why__list">
              {(whyChoose?.bulletPoints || []).map((point, idx) => (
                <motion.li 
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                >
                  <span className="why__icon" aria-hidden="true">
                    ✓
                  </span>{' '}
                  {point}
                </motion.li>
              ))}
            </ul>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <NavLink to="/about" className="why__cta">Learn More About Us</NavLink>
            </motion.div>
          </motion.div>
          <motion.div 
            className="why__mosaic"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {(() => {
              const defaults = ['w1', 'w2', 'w3', 'p4'];
              const images = Array.isArray(whyChoose.images) && whyChoose.images.length
                ? whyChoose.images
                : defaults.map((key) => ({ imageKey: key, imageUrl: '' }));

              return images.slice(0, 4).map((img, idx) => {
                const key = img.imageKey || defaults[idx] || 'w1';
                const url = img.imageUrl
                  ? img.imageUrl
                  : `${process.env.PUBLIC_URL}/assets/images/${key}.png`;

                return (
                  <motion.div
                    key={idx}
                    className="why__img"
                    style={{ backgroundImage: `url(${url})` }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  />
                );
              });
            })()}
          </motion.div>
        </div>
      </motion.section>
      {/* Expert Advice CTA */}
      <motion.section 
        className="cta-banner" 
        aria-labelledby="cta-banner-title"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <motion.div 
          className="cta-banner__inner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 id="cta-banner-title" className="cta-banner__title">{ctaBanner?.title || 'Need Expert Advice?'}</h2>
          <p className="cta-banner__subtitle">{ctaBanner?.subtitle || 'Our team of construction material experts is here to help you choose the right products for your project. Get in touch today!'}</p>
          <motion.div 
            className="cta-banner__actions"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <a 
              href={footerSettings?.companyPrimaryPhone ? `tel:${footerSettings.companyPrimaryPhone.replace(/[^\d+]/g, '')}` : (ctaBanner?.phone?.href || '#')} 
              className="cta-banner__btn cta-banner__btn--primary"
            >
              {footerSettings?.companyPrimaryPhone ? `Call Us: ${footerSettings.companyPrimaryPhone}` : (ctaBanner?.phone?.label || 'Call Us')}
            </a>
            <button 
              onClick={() => {
                const subject = encodeURIComponent('Inquiry from Tiwari Syndicate Website');
                const body = encodeURIComponent('Dear Tiwari Syndicate Team,\n\nI would like to inquire about your products/services.\n\nPlease provide me with more information.\n\nThank you!');
                const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=enquiry@tiwarisyndicate.com&su=${subject}&body=${body}`;
                window.open(gmailUrl, '_blank');
              }}
              className="cta-banner__btn cta-banner__btn--ghost"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: '13px',
                background: 'rgba(255,255,255,0.14)',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.28)',
                cursor: 'pointer'
              }}
            >
              {ctaBanner?.email?.label || 'Email enquiry'}
            </button>
          </motion.div>
        </motion.div>
      </motion.section>
          </section>
  );
}
